import { defineNuxtRouteMiddleware, createError, guestHost, useAuth, useNuxtApp, useRuntimeConfig } from "#imports";
import { redirectToSignIn } from "../utils/redirectToSignin.js";
import { redirectToWhenUnlogged } from "../internals/redirectToWhenUnlogged.js";
import { safelyNavigateTo } from "@malt/utils";
import { getQuery } from "ufo";
import { callWithNuxt } from "nuxt/app";
export default defineNuxtRouteMiddleware(async (to, from) => {
  const nuxtApp = useNuxtApp();
  const { $pinia } = nuxtApp;
  const baseURL = useRuntimeConfig().app.baseURL;
  const store = useAuth($pinia);
  const host = guestHost();
  function shouldRedirectUserTo(to2, pathWhereToRedirectUser) {
    const currentPath2 = baseURL + to2.path;
    return currentPath2 !== pathWhereToRedirectUser && (pathWhereToRedirectUser === "/" || !currentPath2.startsWith(pathWhereToRedirectUser));
  }
  const currentPath = baseURL + to.path;
  const logger = nuxtApp.$logger;
  if (to.meta.enforcedIdentity != null) {
    const enforcedIdentitySpec = to.meta.enforcedIdentity(to);
    let isLogged, hasValidIdentity, pathWhereToRedirectUser;
    try {
      ({ isLogged, hasValidIdentity, pathWhereToRedirectUser } = await callWithNuxt(
        nuxtApp,
        () => store.tryToEnforceIdentity(enforcedIdentitySpec, to.meta.allowAdminIfNoEnforcedIdentity)
      ));
      if (pathWhereToRedirectUser && shouldRedirectUserTo(to, pathWhereToRedirectUser)) {
        return callWithNuxt(nuxtApp, () => safelyNavigateTo({ path: pathWhereToRedirectUser }, { externalToThisApp: true }));
      }
    } catch (e) {
      logger.error(`Failed to enforce identity, ${e}`, e);
      return callWithNuxt(nuxtApp, redirectToSignIn);
    }
    if (!isLogged) {
      if (to.meta.allowUnloggedIfNoEnforcedIdentity) {
        return;
      }
      logger.info(`Cannot enforce identity for unlogged user`);
      return callWithNuxt(nuxtApp, redirectToSignIn);
    }
    if (!hasValidIdentity) {
      throw createError({ statusCode: 403, statusMessage: "Invalid identity" });
    }
    if (store.currentIdentityIsUndefined && to.meta.forceQualificationOfUndefinedIdentity && !currentPath.startsWith("/account/membership")) {
      return callWithNuxt(nuxtApp, () => safelyNavigateTo({ path: "/account/membership" }, { externalToThisApp: true }));
    }
  }
  if (typeof to.meta.middleware === "string" && to.meta.middleware === "auth" || Array.isArray(to.meta.middleware) && to.meta.middleware.includes("auth")) {
    try {
      const requiredRoles = to.meta.roles ?? [];
      const requiredIdentity = to.meta.identity ?? "ANY";
      const allowImmersion = to.meta.allowImmersion ?? false;
      const forbidAccessIfCompanyIsSuspended = to.meta.forbidAccessIfCompanyIsSuspended ?? false;
      const { isLogged, pathWhereToRedirectUser } = await callWithNuxt(nuxtApp, store.checkIfLoggedOrIfNeedingRedirect);
      const hasAdminRole = store?.user?.roles?.includes("ROLE_ADMIN");
      const queryParams = getQuery(to.fullPath);
      const isInImmersion = queryParams.identityId !== void 0 || queryParams.immersion !== void 0;
      const canBypassIdentityRedirection = allowImmersion && hasAdminRole && isInImmersion;
      if (pathWhereToRedirectUser && shouldRedirectUserTo(to, pathWhereToRedirectUser)) {
        return callWithNuxt(nuxtApp, () => safelyNavigateTo({ path: pathWhereToRedirectUser }, { externalToThisApp: true }));
      }
      if (!isLogged) {
        if (to.query?.["x-auth"]) {
          return;
        }
        if (to.meta.redirectToWhenUnlogged) {
          return redirectToWhenUnlogged(to, from);
        }
        return callWithNuxt(nuxtApp, redirectToSignIn);
      } else if (forbidAccessIfCompanyIsSuspended && store.isCompanySuspended) {
        return callWithNuxt(nuxtApp, () => safelyNavigateTo("/client-settings/restricted", { externalToThisApp: true }));
      } else if (store.currentIdentityIsUndefined && to.meta.forceQualificationOfUndefinedIdentity && !currentPath.startsWith("/account/membership")) {
        return callWithNuxt(nuxtApp, () => safelyNavigateTo({ path: "/account/membership" }, { externalToThisApp: true }));
      } else if (!store.hasEnoughPrivilege(requiredRoles)) {
        return callWithNuxt(nuxtApp, () => safelyNavigateTo(host, { externalToThisApp: true }));
      } else if (!store.hasCorrectIdentity(requiredIdentity) && !canBypassIdentityRedirection) {
        if (to.meta.identityRedirect instanceof Function) {
          const redirectUrl2 = `${host}${to.meta.identityRedirect(to)}`;
          return callWithNuxt(nuxtApp, () => safelyNavigateTo(redirectUrl2, { externalToThisApp: true }));
        }
        const redirectUrl = `${host}${to.meta.identityRedirect || ""}`;
        return callWithNuxt(nuxtApp, () => safelyNavigateTo(redirectUrl, { externalToThisApp: true }));
      }
    } catch (e) {
      logger.error(`Failed to check if logged or needing redirect ${e}`, e);
      return callWithNuxt(nuxtApp, redirectToSignIn);
    }
  }
});
